import {
    Dialog,
    DialogContent,
    DialogTrigger,
} from '@src/design-system/dialog';
import { AddSquare, CloseCircle, Edit2, Menu, Trash } from 'iconsax-react';
import { useState } from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import { queryClient } from '@core/api/query-client';
import { QUERY_KEYS } from '@shared/constants/query-keys';
import type { TApiResponse } from '@shared/types';
import type { TUniversityStatus } from '../../types';
import { Input } from '@src/design-system/input';
import { CircleCheck, LoaderCircle } from 'lucide-react';
import { Button } from '@src/design-system/button';
import { DeleteConfirmDialog } from '@src/design-system/molecules/delete-confirm-dialog-button';

import { HasPermission } from '@features/roles/guards/has-permission';
import { entities } from '@shared/constants/entities';
import {
    Sortable,
    SortableContent,
    SortableItem,
    SortableItemHandle,
} from '@src/design-system/sortable';
import { InfiniteList } from '@src/design-system/molecules/infinite-list';
import universityStatusesApi from '../../services/university-statuses-api';
import EditUniversityStatusInput from './components/edit-status-input';
import universitiesApi from '../../services/universities-api';

function reorderByIds<T extends { id: number }>(
    items: T[],
    ids: number[]
): T[] {
    const byId = new Map(items.map((i) => [i.id, i]));
    return ids.map((id) => byId.get(id)).filter(Boolean) as T[];
}

const ManageStatusesDialog = () => {
    const [addStatusOpen, setAddStatusOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [statusValue, setStatusValue] = useState<string>('');
    const [orderedIds, setOrderedIds] = useState<number[] | null>(null);

    const { data } = universityStatusesApi.useFetchInfinite();

    const {
        mutateAsync: createUniversityStatus,
        isPending: createUniversityStatusPending,
    } = universityStatusesApi.useCreate();
    const {
        mutateAsync: deleteUniversityStatus,
        isPending: deleteUniversityStatusPending,
    } = universityStatusesApi.useDelete();

    const { mutate: orderUniversityStatuses } =
        universitiesApi.useReorderUniversityStatuses();

    const handleAddStatus = () => {
        if (statusValue) {
            createUniversityStatus({ name: statusValue }).then(() => {
                setStatusValue('');
                setAddStatusOpen(false);
            });
        }
    };

    const statusesList = data?.pages.flatMap((p) => p.results);
    const serverOrderIds = statusesList?.map((i) => i.id) ?? [];
    const displayOrderIds = orderedIds ?? serverOrderIds;

    const handleOrderChange = (newIds: number[]) => {
        const previousOrder = [...(orderedIds ?? serverOrderIds)];
        setOrderedIds(newIds);
        orderUniversityStatuses(newIds, {
            onSuccess: () => {
                const updater = (
                    old: InfiniteData<TApiResponse<TUniversityStatus>> | undefined
                ) => {
                    if (!old?.pages?.length) return old;
                    const allResults = old.pages.flatMap((p) => p.results);
                    const reordered = reorderByIds(allResults, newIds);
                    return {
                        ...old,
                        pages: old.pages.map((page, i) =>
                            i === 0 ? { ...page, results: reordered } : page
                        ),
                    };
                };
                queryClient.setQueriesData(
                    {
                        queryKey: [QUERY_KEYS.getUniversityStatuses, 'infinite'],
                        predicate: () => true,
                    },
                    updater
                );
                setOrderedIds(null);
            },
            onError: () => setOrderedIds(previousOrder),
        });
    };

    return (
        <div className='p-4'>
            <div className='flex justify-between mb-2 font-medium items-center'>
                <span className='font-semibold text-sm text-gray-900'>
                    Status ({statusesList?.length})
                </span>

                <HasPermission
                    entity={entities.university}
                    action='add'
                >
                    <span
                        className='mr-2 cursor-pointer underline flex items-center gap-1 text-xs text-primary-700'
                        onClick={() => setAddStatusOpen(true)}
                    >
                        <AddSquare size={16} /> Add Status
                    </span>
                </HasPermission>
            </div>

            <Sortable
                value={displayOrderIds}
                onValueChange={handleOrderChange}
            >
                <SortableContent className='flex-1'>
                    {addStatusOpen && (
                        <div className='bg-transparent mb-4 hover:bg-transparent focus:bg-transparent'>
                            <div className='flex gap-2 items-center w-full'>
                                <Input
                                    size='sm'
                                    className='w-full'
                                    inputClassName='w-full'
                                    onChange={(e) => setStatusValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddStatus();
                                        }
                                    }}
                                    suffexIcon={
                                        <div className='text-black flex gap-1 items-center'>
                                            <CloseCircle
                                                onClick={() => setAddStatusOpen(false)}
                                                size={16}
                                                className='cursor-pointer opacity-80 hover:opacity-100'
                                            />
                                            <CircleCheck
                                                onClick={() => handleAddStatus()}
                                                size={18}
                                                className='cursor-pointer stroke-[1.5px] text-white fill-green-500'
                                            />
                                        </div>
                                    }
                                />

                                {createUniversityStatusPending && (
                                    <LoaderCircle
                                        className='animate-spin'
                                        size={20}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    <div>
                        <InfiniteList
                            enableSearch
                            searchParamName='name'
                            fetchInfiniteData={universityStatusesApi.useFetchInfinite}
                            transformItems={(items) => reorderByIds(items, displayOrderIds)}
                            props={{
                                listProps: { className: 'gap-2' },
                                listContainerProps: {
                                    className:
                                        'p-0 flex-1 max-h-[40vh] overflow-y-auto scrollbar-gray',
                                },
                                searchContainerProps: {
                                    className: 'p-0',
                                },
                                searchInputProps: {
                                    placeholder: 'Search in Status',
                                },
                            }}
                            renderItem={(status) => {
                                const id = status.id;
                                const isEditing = editingId === id;
                                const name = status.name;

                                return (
                                    <SortableItem
                                        className='group flex gap-2'
                                        value={id}
                                    >
                                        <HasPermission
                                            entity={entities.university}
                                            action='change'
                                        >
                                            <SortableItemHandle
                                                autoFocus={false}
                                                asChild
                                            >
                                                <button>
                                                    <Menu
                                                        size={20}
                                                        variant='Bold'
                                                        className='text-primary-500 shrink-0'
                                                    />
                                                </button>
                                            </SortableItemHandle>
                                        </HasPermission>

                                        <div className='flex items-center gap-2 w-full'>
                                            <EditUniversityStatusInput
                                                isEditing={isEditing}
                                                onClose={() => setEditingId(null)}
                                                value={name}
                                                key={id}
                                                editingId={id}
                                            />

                                            {!isEditing && (
                                                <div className='flex items-center'>
                                                    <HasPermission
                                                        entity={entities.university}
                                                        action='change'
                                                    >
                                                        <Button
                                                            variant='ghost'
                                                            size={'iconXs'}
                                                            className='hidden group-hover:flex'
                                                            onClick={() => setEditingId(id)}
                                                        >
                                                            <Edit2
                                                                className='text-gray-800 cursor-pointer'
                                                                size={22}
                                                                variant='TwoTone'
                                                            />
                                                        </Button>
                                                    </HasPermission>

                                                    <HasPermission
                                                        entity={entities.university}
                                                        action='delete'
                                                    >
                                                        <Dialog>
                                                            <DialogTrigger asChild>
                                                                <Button
                                                                    variant='ghost'
                                                                    size={'iconXs'}
                                                                >
                                                                    <Trash
                                                                        size={20}
                                                                        color='#F05252'
                                                                        cursor='pointer'
                                                                        className='hidden group-hover:block'
                                                                    />
                                                                </Button>
                                                            </DialogTrigger>

                                                            <DialogContent align='center'>
                                                                <DeleteConfirmDialog
                                                                    key='university status'
                                                                    title='Are you sure you want to delete this university status?'
                                                                    isLoading={deleteUniversityStatusPending}
                                                                    onConfirm={(closeModal) =>
                                                                        deleteUniversityStatus(
                                                                            { id },
                                                                            {
                                                                                onSuccess: closeModal,
                                                                            }
                                                                        )
                                                                    }
                                                                />
                                                            </DialogContent>
                                                        </Dialog>
                                                    </HasPermission>
                                                </div>
                                            )}
                                        </div>
                                    </SortableItem>
                                );
                            }}
                        />
                    </div>
                </SortableContent>
            </Sortable>
        </div>
    );
};

export default ManageStatusesDialog;
